import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./blogs.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";
import parse from "html-react-parser";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Import the Quill stylesheet

interface User {
  _id: string;
  title: string;
  content: string;
  category: string;
  imageUrl: string;
  isPremium: boolean;
  createdAt: Date;
  isActive: boolean;
}

const Blogs = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [showCreateBlogOverlay, setShowCreateBlogOverlay] =
    useState<boolean>(false);
  const [isUpdateBtn, setIsUpdateBtn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [createBtnDisabled, setCreateBtnDisabled] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [blogDetails, setBlogDetails] = useState({
    title: "",
    category: "",
    content: "",
    isPremium: false,
    imageUrl: "",
    isActive: true,
  });
  const [imageFile, setImageFile] = useState<File | null>(null); // For image upload
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);
  const [userId, setUserId] = useState<string>("");

  const [isBlogActive, setisBlogActive] = useState(true);

  const handleToggle = () => {
    setisBlogActive(!isBlogActive);
  };

  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllBlogs();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  const fetchAllBlogs = async () => {
    try {
      const response = await axios.get(`https://api.fitoja.in/api/get-blogs`);
      setUsers(response.data.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  const handleCreateBlog = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!blogDetails.title || !blogDetails.category) {
      toast.error("Please fill in all the required fields!");
      return;
    }

    const formData = new FormData();
    formData.append("title", blogDetails.title);
    formData.append("category", blogDetails.category);
    formData.append("content", blogDetails.content);
    formData.append(
      "isPremium",
      blogDetails.isPremium.toString() == "true" ? "true" : "false"
    );
    formData.append(
      "isActive",
      isUpdateBtn ? blogDetails.isActive.toString() : isBlogActive.toString()
    );
    formData.append("imageUrl", imageFile as File);

    try {
      const response = await axios.post(
        "https://api.fitoja.in/api/create-blog",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data.success) {
        toast.success("Blog created successfully!");
        setShowCreateBlogOverlay(false);
        setBlogDetails({
          title: "",
          category: "",
          content: "",
          isPremium: false,
          imageUrl: "",
          isActive: true,
        });
        fetchAllBlogs(); // Refresh blog list
        setCreateBtnDisabled(false);
        // window.location.reload();
      }
    } catch (error) {
      console.error("Failed to create blog:", error);
      toast.error("Failed to create blog. Please try again.");
      setCreateBtnDisabled(false);
    }
  };

  const filteredUsers = users.filter((user) =>
    user.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
  );

  const handleEditBlog = (blog: User) => {
    setIsUpdateBtn(true);
    setBlogDetails({
      title: blog.title,
      category: blog.category,
      content: blog.content,
      isPremium: blog.isPremium,
      imageUrl: blog.imageUrl,
      isActive: blog.isActive,
    });
    setShowCreateBlogOverlay(true);
  };

  const handleDeleteBlog = async (blogId: string) => {
    try {
      const response = await axios.delete(
        `https://api.fitoja.in/api/delete-blog/${blogId}`
      );
      if (response.data.success) {
        toast.success("Blog deleted successfully!");
        setShowDeleteConfirmation(false);
        fetchAllBlogs(); // Refresh blog list
      }
    } catch (error) {
      console.error("Failed to delete blog:", error);
      toast.error("Failed to delete blog. Please try again.");
    }
  };

  return (
    <div className="blogs">
      <ToastContainer theme="colored" />
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}
      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} alt="Logo" />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                  alt="Close"
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          <div className="search-box">
            <h2>Blogs/Trending Topics</h2>
            <input
              type="text"
              placeholder="Search blog by Title"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div
            className="create-blog-btn"
            onClick={() => {
              setIsUpdateBtn(false);
              setShowCreateBlogOverlay(true);
            }}
          >
            <span>Create a new Blog</span>
          </div>

          {showCreateBlogOverlay && (
            <div className="create-blog-overlay">
              <div className="create-blog-content">
                {isUpdateBtn ? "" : <h3>Create a New Blog</h3>}
                <form
                  style={{ display: "grid", gap: "10px", alignItems: "start" }}
                  onSubmit={(e) => {
                    setBlogDetails({
                      title: "",
                      category: "",
                      content: "",
                      isPremium: false,
                      imageUrl: "",
                      isActive: true,
                    });
                    handleCreateBlog(e);
                  }}
                >
                  <div className="switch-container">
                    <label className="switch">
                      {!isUpdateBtn ? (
                        <input
                          type="checkbox"
                          checked={isBlogActive}
                          onChange={handleToggle}
                        />
                      ) : (
                        <input
                          type="checkbox"
                          checked={blogDetails.isActive}
                          onChange={(e) =>
                            setBlogDetails({
                              ...blogDetails,
                              isActive: e.target.checked,
                            })
                          }
                        />
                      )}
                      <span className="slider round"></span>
                    </label>
                    {isUpdateBtn ? (
                      <p className="active-inactive-text">
                        {blogDetails.isActive ? "Active" : "Inactive"}
                      </p>
                    ) : (
                      <p className="active-inactive-text">
                        {isBlogActive ? "Active" : "Inactive"}
                      </p>
                    )}
                  </div>
                  {isUpdateBtn ? (
                    <span style={{ fontSize: "22px", fontWeight: "600" }}>
                      {blogDetails.title}
                    </span>
                  ) : (
                    <div>
                      <label>Title</label>
                      <input
                        type="text"
                        value={blogDetails.title}
                        onChange={(e) =>
                          setBlogDetails({
                            ...blogDetails,
                            title: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  )}
                  <div>
                    <label>Category</label>
                    <input
                      type="text"
                      value={blogDetails.category}
                      onChange={(e) =>
                        setBlogDetails({
                          ...blogDetails,
                          category: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  {/* <div>
                    <label>Content</label>
                    <textarea
                      value={blogDetails.content}
                      onChange={(e) =>
                        setBlogDetails({
                          ...blogDetails,
                          content: e.target.value,
                        })
                      }
                      required
                    ></textarea>
                  </div> */}
                  <div>
                    <label>Content</label>
                    <ReactQuill
                      value={blogDetails.content}
                      onChange={(value: any) =>
                        setBlogDetails({
                          ...blogDetails,
                          content: value,
                        })
                      }
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, 4, 5, 6, false] }],
                          ["bold", "italic", "underline"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [{ align: [] }],
                          ["image"],
                          ["clean"], // Remove formatting
                        ],
                      }}
                      formats={[
                        "header",
                        "bold",
                        "italic",
                        "underline",
                        "blockquote",
                        "code-block",
                        "list",
                        "bullet",
                        "align",
                        "link",
                        "image",
                      ]}
                    />
                  </div>
                  <div>
                    <label>Blog Type</label>
                    <select
                      value={blogDetails.isPremium.toString()}
                      onChange={(e) =>
                        setBlogDetails({
                          ...blogDetails,
                          isPremium: e.target.value === "true",
                        })
                      }
                      required
                    >
                      <option value="false">Non-Premium</option>
                      <option value="true">Premium</option>
                    </select>
                  </div>
                  <div>
                    <img
                      style={{ width: "100%" }}
                      src={blogDetails.imageUrl}
                    ></img>
                  </div>
                  <div>
                    <label>
                      {isUpdateBtn
                        ? "Upload new image (Optional)"
                        : "Upload Image"}
                    </label>
                    <input
                      type="file"
                      name="imageUrl"
                      onChange={(e) =>
                        setImageFile(e.target.files ? e.target.files[0] : null)
                      }
                    />
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setBlogDetails({
                          title: "",
                          category: "",
                          content: "",
                          isPremium: false,
                          imageUrl: "",
                          isActive: true,
                        });
                        setShowCreateBlogOverlay(false);
                      }}
                    >
                      {isUpdateBtn ? "Discard Changes" : "Cancel"}
                    </button>
                    {createBtnDisabled ? (
                      <button style={{ cursor: "no-drop" }}>
                        {isUpdateBtn ? "Update Blog" : "Create Blog"}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        onClick={() => {
                          setCreateBtnDisabled(true);
                        }}
                      >
                        {isUpdateBtn ? "Save Changes" : "Create Blog"}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="user-list">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div key={user._id} className="user-card">
                  <div className="user-details">
                    <span>
                      <img
                        className="user-details-img"
                        src={user.imageUrl}
                        alt="Blog"
                      />
                    </span>
                    <div>
                      <p style={{ textAlign: "start" }}>
                        <strong>Title:</strong> <span>{user.title}</span>
                      </p>
                      <p
                        style={{
                          textAlign: "start",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: "5",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <strong>Content:</strong>{" "}
                        <span>{parse(user.content)}</span>
                      </p>
                      <p>
                        <strong>Category:</strong> {user.category}
                      </p>
                      <p>
                        <strong>Type:</strong>{" "}
                        {user.isPremium ? "Premium" : "Non-Premium"}
                      </p>
                    </div>
                  </div>

                  <div
                    style={{ display: "flex", gap: "20px", marginLeft: "auto" }}
                  >
                    <button
                      className="edit-button"
                      onClick={() => handleEditBlog(user)}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-button"
                      onClick={() => {
                        setShowDeleteConfirmation(true);
                        setUserId(user._id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>No blogs found.</p>
            )}
          </div>

          {showDeleteConfirmation && (
            <div className="delete-modal">
              <div className="delete-modal-content">
                <h2>Delete Confirmation</h2>
                <p>Are you sure you want to delete this diet Plan?</p>
                <div style={{ display: "flex", gap: "20px" }}>
                  <button
                    className="confirm-delete-button"
                    onClick={() => setShowDeleteConfirmation(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="cancel-button"
                    onClick={() => handleDeleteBlog(userId)}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Blogs;
